import { Brain, CheckCircle, Clock, XCircle } from "lucide-react";
import React from "react";
import { cn } from "src/common/utils";
import { Badge } from "src/components/ui/badge";

type StatusType =
  | "COMPLETED"
  | "IN_PROGRESS"
  | "ERROR"
  | "SCHEDULED"
  | "PENDING"
  | "ERRORED"
  | "STOPPED";

interface JobRunStatusProps {
  /** The status of the job run */
  status: StatusType | string;
  /** Optional size variant */
  size?: "sm" | "xs";
  /** Whether to show as badge (default) or text */
  variant?: "badge" | "text";
  /** Additional CSS classes */
  className?: string;
}

export function JobRunStatus({
  status,
  size = "xs",
  variant = "badge",
  className,
}: JobRunStatusProps) {
  // Map status to appropriate color and icon
  const getStatusConfig = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return {
          color: "green",
          label: "Completed",
          icon: CheckCircle,
        };
      case "IN_PROGRESS":
      case "RUNNING":
        return {
          color: "orange",
          label: "Running",
          icon: Brain,
        };
      case "ERRORED":
      case "STOPPED":
      case "ERROR":
        return {
          color: "red",
          label: "Errored",
          icon: XCircle,
        };
      case "SCHEDULED":
      case "PENDING":
        return {
          color: "blue",
          label: "Scheduled",
          icon: Clock,
        };
      default:
        return {
          color: "gray",
          label: status,
          icon: Clock,
        };
    }
  };

  const { color, label, icon: Icon } = getStatusConfig(status);
  const isThinking = status === "IN_PROGRESS" || status === "RUNNING";

  // For badge variant
  if (variant === "badge") {
    return (
      <Badge
        variant={color as any}
        className={cn(className)}
        size={size}
      >
        <Icon
          className={cn("mr-1 h-3 w-3", isThinking && "animate-bounce")}
        />
        {label}
      </Badge>
    );
  }

  // For text variant
  const textSizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
  };

  const iconSizeClasses = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
  };

  const textColorClasses = {
    green: "text-green-500",
    orange: "text-amber-500",
    red: "text-red-500",
    blue: "text-blue-500",
    gray: "text-gray-500",
  };

  return (
    <div className={cn("flex items-center", textSizeClasses[size], className)}>
      <Icon
        className={cn(
          "mr-1",
          iconSizeClasses[size],
          textColorClasses[color as keyof typeof textColorClasses],
          isThinking && "animate-bounce",
        )}
      />
      <span className={textColorClasses[color as keyof typeof textColorClasses]}>
        {label}
      </span>
    </div>
  );
}
